import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { ArrowLeft, Brain, BookOpenCheck, ChevronLeft, ChevronRight, ClipboardList, LayoutDashboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from '../shared/MobileResponsive';
import { PersianOverlayLoader } from '../shared/PersianLoader';
import { LearnerPageHero } from '../shared/LearnerPageHero';
import { contentService, EnhancedQuizResultDto, QuizFlowItemDto } from '../../services/contentService';
import { useNotifications } from '../feedback/NotificationSystem';
import { QuizModal } from './QuizModal';
import { levelProgressionService, KnowledgePlacementRecommendationDto } from '../../services/levelProgressionService';
import { emitKnowledgePlacementUpdated } from '../../utils/knowledgePlacementStorage';
import { useLocale } from '../../hooks/useLocale';
import { courseTitleFromFlowContext, formatFlowContextSubtitle } from '../../utils/catalogFlowCourse';
import { FlowContextBreadcrumb } from '../shared/FlowContextBreadcrumb';
import { isDesignedForNonFarsiUsers } from '../../utils/nonFarsiContentHint';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import {
  TOUR_PAGE_END_EVENT,
  TOUR_TARGET_FOCUS_EVENT,
  type TourPageEndEventDetail,
  type TourTargetEventDetail,
} from '../../learnerTour/tourTargetEvents';

type WizardStep = 'intro' | 'pick-course' | 'run';

const STEP_ORDER: Record<WizardStep, number> = {
  intro: 0,
  'pick-course': 1,
  run: 2,
};

const STEP_SLIDE_PX = 56;
const STEP_TRANSITION = { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const };

function buildStepVariants(isRtl: boolean): Variants {
  // LTR forward: exit left / enter from right. RTL forward: exit right / enter from left.
  const axis = isRtl ? -1 : 1;
  return {
    enter: (dir: number) => ({
      x: dir * STEP_SLIDE_PX * axis,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: -dir * STEP_SLIDE_PX * axis,
      opacity: 0,
    }),
  };
}

interface CoursePick {
  courseId: number;
  title: string;
}

export interface KnowledgePlacementWizardProps {
  userId: string;
  /** Exit / browser-style back — returns to the view that opened placement (e.g. profile). */
  onBack: () => void;
  /** Hero / finished-view “Back to dashboard” — always opens the learner dashboard. */
  onBackToDashboard: () => void;
  onNavigateToLesson: (lessonId: number) => void;
}

function lessonQuizzesForCourse(items: QuizFlowItemDto[], courseId: number): QuizFlowItemDto[] {
  return items.filter(
    (i) =>
      i.kind === 'lesson' &&
      i.courseId === courseId &&
      (i.includeInKnowledgePlacement ?? true)
  );
}

export const KnowledgePlacementWizard: React.FC<KnowledgePlacementWizardProps> = ({
  userId,
  onBack,
  onBackToDashboard,
  onNavigateToLesson,
}) => {
  const { t } = useTranslation('learning');
  const { direction, locale } = useLocale();
  const { pickTitle } = useLocalizedContent();
  const isRtl = direction === 'rtl';
  const { showSuccess, showError } = useNotifications();
  void userId;
  const [step, setStep] = useState<WizardStep>('intro');
  const stepRef = useRef<WizardStep>('intro');
  stepRef.current = step;
  /** 1 = forward through wizard, -1 = back. Drives slide enter/exit. */
  const [slideDir, setSlideDir] = useState(1);
  const stepVariants = useMemo(() => buildStepVariants(isRtl), [isRtl]);
  const reduceMotion = prefersReducedMotion();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<QuizFlowItemDto[]>([]);
  const [courses, setCourses] = useState<CoursePick[]>([]);
  const coursesRef = useRef(courses);
  coursesRef.current = courses;
  const [courseId, setCourseId] = useState<number | null>(null);
  const courseIdRef = useRef(courseId);
  courseIdRef.current = courseId;
  /** Course selected before the guide slides back to intro or the course list. */
  const tourCourseIdRef = useRef<number | null>(null);
  const [index, setIndex] = useState(0);
  const [quizItem, setQuizItem] = useState<QuizFlowItemDto | null>(null);
  const [finishedView, setFinishedView] = useState<'recommended' | 'advanced' | null>(null);
  const [reco, setReco] = useState<KnowledgePlacementRecommendationDto | null>(null);
  const [runId, setRunId] = useState<number | null>(null);
  const [pendingFailedQuiz, setPendingFailedQuiz] = useState<QuizFlowItemDto | null>(null);
  const [pendingPassedQuiz, setPendingPassedQuiz] = useState<QuizFlowItemDto | null>(null);
  const indexRef = useRef(index);
  indexRef.current = index;
  const finishedViewRef = useRef(finishedView);
  finishedViewRef.current = finishedView;
  const pendingFailedRef = useRef(pendingFailedQuiz);
  pendingFailedRef.current = pendingFailedQuiz;
  const pendingPassedRef = useRef(pendingPassedQuiz);
  pendingPassedRef.current = pendingPassedQuiz;
  /** Wizard page captured the first time this guide run focuses the placement screen. */
  const tourSnapshotRef = useRef<{
    step: WizardStep;
    courseId: number | null;
    index: number;
    finishedView: 'recommended' | 'advanced' | null;
    pendingFailedQuiz: QuizFlowItemDto | null;
    pendingPassedQuiz: QuizFlowItemDto | null;
  } | null>(null);
  const clearCourseTimerRef = useRef<number | null>(null);

  const goToStep = useCallback((next: WizardStep) => {
    const current = stepRef.current;
    if (next === current) return;
    setSlideDir(STEP_ORDER[next] >= STEP_ORDER[current] ? 1 : -1);
    setStep(next);
    // Keep courseId during the exit slide so the run panel does not blank mid-animation.
    if (clearCourseTimerRef.current != null) {
      window.clearTimeout(clearCourseTimerRef.current);
      clearCourseTimerRef.current = null;
    }
    if (next === 'pick-course' || next === 'intro') {
      clearCourseTimerRef.current = window.setTimeout(
        () => {
          clearCourseTimerRef.current = null;
          setCourseId(null);
        },
        prefersReducedMotion() ? 0 : Math.round(STEP_TRANSITION.duration * 1000) + 40
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      if (clearCourseTimerRef.current != null) {
        window.clearTimeout(clearCourseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      const key = detail?.targetKey;
      if (!key || detail.pageKey !== 'knowledge-placement') return;
      if (!tourSnapshotRef.current) {
        tourSnapshotRef.current = {
          step: stepRef.current,
          courseId: courseIdRef.current,
          index: indexRef.current,
          finishedView: finishedViewRef.current,
          pendingFailedQuiz: pendingFailedRef.current,
          pendingPassedQuiz: pendingPassedRef.current,
        };
        tourCourseIdRef.current = courseIdRef.current;
      }
      const intro =
        key === 'knowledge-placement-intro' || key === 'knowledge-placement-choose-course';
      const picking = key === 'knowledge-placement-courses';
      const running =
        key === 'knowledge-placement-quiz' ||
        key === 'knowledge-placement-take-quiz' ||
        key === 'knowledge-placement-skip' ||
        key === 'knowledge-placement-change-course';
      if (!intro && !picking && !running) return;
      flushSync(() => {
        setFinishedView(null);
        if (courseIdRef.current != null) tourCourseIdRef.current = courseIdRef.current;
        if (intro) {
          goToStep('intro');
          return;
        }
        if (picking) {
          goToStep('pick-course');
          return;
        }
        if (clearCourseTimerRef.current != null) {
          window.clearTimeout(clearCourseTimerRef.current);
          clearCourseTimerRef.current = null;
        }
        setPendingFailedQuiz(null);
        setPendingPassedQuiz(null);
        const restoreId =
          courseIdRef.current ?? tourCourseIdRef.current ?? coursesRef.current[0]?.courseId ?? null;
        if (courseIdRef.current == null && tourCourseIdRef.current == null) setIndex(0);
        setCourseId(restoreId);
        goToStep('run');
      });
    };
    const onTourEnd = (event: Event) => {
      const detail = (event as CustomEvent<TourPageEndEventDetail>).detail;
      if (detail?.pageKey !== 'knowledge-placement') return;
      const snap = tourSnapshotRef.current;
      tourSnapshotRef.current = null;
      tourCourseIdRef.current = null;
      if (!snap) return;
      flushSync(() => {
        if (clearCourseTimerRef.current != null) {
          window.clearTimeout(clearCourseTimerRef.current);
          clearCourseTimerRef.current = null;
        }
        setPendingFailedQuiz(snap.pendingFailedQuiz);
        setPendingPassedQuiz(snap.pendingPassedQuiz);
        setIndex(snap.index);
        setFinishedView(snap.finishedView);
        setCourseId(snap.courseId);
        goToStep(snap.step);
      });
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    window.addEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    return () => {
      window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
      window.removeEventListener(TOUR_PAGE_END_EVENT, onTourEnd);
    };
  }, [goToStep]);

  const courseFallback = useCallback(
    (id: number) => t('placementWizard.courseFallback', { id }),
    [t]
  );

  const reload = useCallback(async () => {
    const list = await contentService.getQuizFlowItems();
    setItems(Array.isArray(list) ? list : []);
    const lessonItems = list.filter((i: QuizFlowItemDto) => i.kind === 'lesson');
    const map = new Map<number, string>();
    for (const li of lessonItems) {
      if (!map.has(li.courseId)) {
        const title = courseTitleFromFlowContext(li.contextSubtitle, li.courseId, li.contextSubtitleFa, locale);
        map.set(li.courseId, title || courseFallback(li.courseId));
      }
    }
    setCourses(
      Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([cid, title]) => ({ courseId: cid, title }))
    );
  }, [courseFallback, locale]);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        setLoading(true);
        await reload();
      } catch (e) {
        if (!c) showError(e instanceof Error ? e.message : t('placementWizard.loadCoursesFailed'));
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [reload, showError, t]);

  const chain = useMemo(() => {
    if (courseId == null) return [];
    return lessonQuizzesForCourse(items, courseId);
  }, [items, courseId]);

  const current = chain[index] ?? null;
  const total = chain.length;
  const positionLabel =
    total > 0
      ? t('placementWizard.lessonQuizPosition', { current: Math.min(index + 1, total), total })
      : '';
  const hasPendingDecision = Boolean(pendingFailedQuiz || pendingPassedQuiz);

  const goNextInChain = useCallback(() => {
    setIndex((i) => Math.min(i + 1, Math.max(chain.length - 1, 0)));
  }, [chain.length]);

  const goPrevInChain = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const skipTooEasy = useCallback(() => {
    if (index >= chain.length - 1) {
      showError(t('placementWizard.lastQuizError'));
      return;
    }
    goNextInChain();
  }, [chain.length, index, goNextInChain, showError, t]);

  const skipTooEasyAndPersist = useCallback(async () => {
    if (!current) {
      skipTooEasy();
      return;
    }
    try {
      let activeRunId = runId;
      if (activeRunId == null && courseId != null) {
        const started = await levelProgressionService.startKnowledgePlacementRun(courseId);
        activeRunId = started.runId;
        setRunId(started.runId);
      }
      if (activeRunId == null) {
        skipTooEasy();
        return;
      }
      await levelProgressionService.recordKnowledgePlacementAttempt({
        runId: activeRunId,
        lessonQuizId: current.quizId,
        decision: 'skipped_easy'
      });
      emitKnowledgePlacementUpdated();
    } catch (e) {
      console.warn('Failed to persist skipped-easy decision:', e);
    }
    skipTooEasy();
  }, [courseId, current, runId, skipTooEasy]);

  const openCurrentQuiz = useCallback(() => {
    if (!current) return;
    setQuizItem(current);
  }, [current]);

  const handleQuizFlowComplete = useCallback(
    async (result: EnhancedQuizResultDto) => {
      setQuizItem(null);
      if (!current || courseId == null) return;

      let activeRunId = runId;
      if (activeRunId == null) {
        const started = await levelProgressionService.startKnowledgePlacementRun(courseId);
        activeRunId = started.runId;
        setRunId(started.runId);
      }
      if (activeRunId == null) return;

      await levelProgressionService.recordKnowledgePlacementAttempt({
        runId: activeRunId,
        lessonQuizId: current.quizId,
        decision: 'attempted',
        scorePercent: result.score,
        passed: result.passed
      });
      emitKnowledgePlacementUpdated();

      if (result.passed) {
        setPendingFailedQuiz(null);
        setPendingPassedQuiz(current);
        showSuccess(t('placementWizard.passedToast', { title: pickTitle(current) }));
        return;
      }

      setPendingPassedQuiz(null);
      setPendingFailedQuiz(current);
    },
    [courseId, current, runId, showSuccess, t]
  );

  const handlePlacementConsiderCurrent = useCallback(async () => {
    if (!current || courseId == null) return;
    let activeRunId = runId;
    if (activeRunId == null) {
      const started = await levelProgressionService.startKnowledgePlacementRun(courseId);
      activeRunId = started.runId;
      setRunId(started.runId);
    }
    if (activeRunId == null) return;
    await levelProgressionService.completeKnowledgePlacementRun(activeRunId, 'recommended', current.quizId);
    const state = await levelProgressionService.getKnowledgePlacementState();
    setReco(state.lastRecommendation ?? {
      courseId,
      courseTitle: courses.find((x) => x.courseId === courseId)?.title ?? courseFallback(courseId),
      levelId: current.levelId ?? 0,
      lessonId: current.lessonId ?? current.viewLessonId ?? 0,
      lessonQuizId: current.quizId,
      quizTitle: pickTitle(current),
      contextSubtitle: formatFlowContextSubtitle(current.contextSubtitle, isRtl, current.contextSubtitleFa),
      evaluatedAt: new Date().toISOString()
    });
    setQuizItem(null);
    setPendingFailedQuiz(null);
    setPendingPassedQuiz(null);
    setFinishedView('recommended');
    emitKnowledgePlacementUpdated();
  }, [courseId, courses, current, courseFallback, runId]);

  const backArrowClass = isRtl ? 'rotate-180' : '';

  /** Placement-specific page hero (Brain icon + watermark; corner flips with locale). */
  const placementPageHero = (
    <LearnerPageHero
      tourId="knowledge-placement-header"
      eyebrow={t('placementWizard.heroEyebrow')}
      title={t('placementWizard.heroTitle')}
      subtitle={t('placementWizard.heroSubtitle')}
      icon={Brain}
      action={
        <button
          type="button"
          onClick={onBackToDashboard}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200/90 bg-white/65 px-3 py-2 text-xs font-semibold text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/85 active:scale-[0.99] dark:border-violet-700/50 dark:bg-violet-950/35 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/50 md:w-auto md:px-4 md:py-2.5 md:text-sm"
        >
          <LayoutDashboard className="h-3.5 w-3.5 md:h-4 md:w-4" aria-hidden />
          <span>{t('placementWizard.backToDashboard')}</span>
        </button>
      }
    />
  );

  return (
    <>
      {loading ? (
        <PersianOverlayLoader isVisible message={t('placementWizard.preparing')} />
      ) : courses.length === 0 ? (
      <div className="min-h-screen bg-gray-50 pb-12 dark:bg-gray-950" dir={direction}>
        <ResponsiveContainer maxWidth="lg" className="space-y-6 py-8">
          <button
            type="button"
            onClick={onBack}
            className={`inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800 dark:text-blue-400`}
          >
            <ArrowLeft className={`h-4 w-4 ${backArrowClass}`} />
            {t('placementWizard.back')}
          </button>
          {placementPageHero}
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <ClipboardList className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="text-gray-700 dark:text-gray-300">
              {t('placementWizard.noQuizzesAvailable')}
            </p>
          </div>
        </ResponsiveContainer>
      </div>
      ) : (
    <div className="min-h-screen bg-gray-50 pb-12 dark:bg-gray-950" dir={direction}>
      <ResponsiveContainer maxWidth="lg" className="space-y-6 py-6">
        <div className={`flex flex-wrap items-center gap-3`}>
          <button
            type="button"
            onClick={onBack}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800`}
          >
            <ArrowLeft className={`h-4 w-4 ${backArrowClass}`} />
            {t('placementWizard.exit')}
          </button>
          <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800 dark:bg-violet-900/50 dark:text-violet-200">
            {t('placementWizard.badge')}
          </span>
        </div>

        {placementPageHero}

        {finishedView === 'recommended' && reco && (
          <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-6 shadow-sm dark:border-emerald-800/60 dark:from-emerald-950/40 dark:to-slate-900">
            <h2 className={`text-xl font-bold text-emerald-950 dark:text-emerald-100`}>{t('placementWizard.suggestedTitle')}</h2>
            <p className={`mt-2 text-sm text-emerald-900/90 dark:text-emerald-200/90`}>
              {t('placementWizard.suggestedDesc')}
            </p>
            <div className="mt-5 rounded-xl border border-emerald-200/70 bg-white/90 p-4 dark:border-emerald-800/50 dark:bg-gray-950/80">
              <p className={`text-sm font-semibold text-gray-900 dark:text-gray-100`}>{reco.contextSubtitle}</p>
              <p className={`mt-1 text-xs text-gray-600 dark:text-gray-400`}>{reco.quizTitle}</p>
            </div>
            <div className={`mt-6 flex flex-wrap gap-3`}>
              <button
                type="button"
                onClick={() => onNavigateToLesson(reco.lessonId)}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-emerald-700"
              >
                {t('placementWizard.openLesson')}
              </button>
              <button type="button" onClick={onBackToDashboard} className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
                {t('placementWizard.backToDashboard')}
              </button>
            </div>
          </div>
        )}

        {finishedView === 'advanced' && (
          <div className="overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 p-6 shadow-sm dark:border-violet-800/60 dark:from-violet-950/30 dark:to-slate-900">
            <h2 className={`text-xl font-bold text-violet-950 dark:text-violet-100`}>{t('placementWizard.strongWork')}</h2>
            <p className={`mt-2 text-sm text-violet-900/90 dark:text-violet-200/90`}>
              {t('placementWizard.advancedDesc')}
            </p>
            <div className={`mt-6 flex flex-wrap gap-3`}>
              <button
                type="button"
                onClick={() => {
                  setFinishedView(null);
                  setIndex(0);
                  goToStep('pick-course');
                }}
                className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700"
              >
                {t('placementWizard.assessAnother')}
              </button>
              <button type="button" onClick={onBackToDashboard} className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
                {t('placementWizard.backToDashboard')}
              </button>
            </div>
          </div>
        )}

        {finishedView === null && (
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={slideDir} initial={false}>
              <motion.div
                key={step}
                custom={slideDir}
                variants={stepVariants}
                initial={reduceMotion ? false : 'enter'}
                animate="center"
                exit={reduceMotion ? undefined : 'exit'}
                transition={reduceMotion ? { duration: 0 } : STEP_TRANSITION}
              >
                {step === 'intro' && (
          <section data-tour-id="knowledge-placement-intro" className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-8 text-white">
              <div className={`flex items-start gap-4`}>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20">
                  <Brain className="h-7 w-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold leading-tight">{t('placementWizard.introTitle')}</h1>
                  <p className="mt-2 max-w-xl text-sm text-white/90">
                    {t('placementWizard.introDesc')}
                  </p>
                </div>
              </div>
            </div>
            <div className={`space-y-3 px-6 py-6 text-sm text-gray-600 dark:text-gray-400`}>
              <div className={`flex gap-2`}>
                <BookOpenCheck className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
                <span>{t('placementWizard.introBullet1')}</span>
              </div>
              <div className={`flex gap-2`}>
                <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
                <span>{t('placementWizard.introBullet2')}</span>
              </div>
              <button
                type="button"
                data-tour-id="knowledge-placement-choose-course"
                onClick={() => goToStep('pick-course')}
                className="mt-4 w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white shadow hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 sm:w-auto sm:px-8"
              >
                {t('placementWizard.chooseCourse')}
              </button>
            </div>
          </section>
                )}

                {step === 'pick-course' && (
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <h2 className={`text-lg font-bold text-gray-900 dark:text-gray-100`}>{t('placementWizard.whichCourse')}</h2>
            <p className={`mt-1 text-sm text-gray-600 dark:text-gray-400`}>{t('placementWizard.whichCourseDesc')}</p>
            <ul data-tour-id="knowledge-placement-courses" className="mt-6 grid gap-3 sm:grid-cols-2">
              {courses.map((c) => (
                <li key={c.courseId}>
                  <button
                    type="button"
                    onClick={() => {
                      setRunId(null);
                      setCourseId(c.courseId);
                      setIndex(0);
                      goToStep('run');
                    }}
                    className={`flex w-full items-center justify-between rounded-xl border border-gray-200 px-4 py-4 transition hover:border-violet-400 hover:bg-violet-50/60 dark:border-gray-600 dark:hover:border-violet-500 dark:hover:bg-violet-950/30 text-start`}
                  >
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{c.title}</span>
                    <ChevronRight className={`h-5 w-5 text-gray-400 ${isRtl ? 'rotate-180' : ''}`} />
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => goToStep('intro')} className={`mt-6 text-sm font-medium text-violet-700 hover:underline dark:text-violet-400 block w-full`}>
              {t('placementWizard.backToIntro')}
            </button>
          </section>
                )}

                {step === 'run' && courseId != null && (
          <section className="space-y-4">
            {total === 0 ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950/40">
                <p className={`text-sm text-amber-950 dark:text-amber-100`}>{t('placementWizard.noLessonQuizzes')}</p>
                <button
                  type="button"
                  onClick={() => goToStep('pick-course')}
                  className={`mt-4 text-sm font-semibold text-amber-900 underline dark:text-amber-200 block w-full`}
                >
                  {t('placementWizard.chooseDifferentCourse')}
                </button>
              </div>
            ) : (
              <>
                {pendingFailedQuiz && (
                  <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
                    <p className={`text-sm font-semibold text-amber-900 dark:text-amber-200`}>
                      {t('placementWizard.failedTitle', { title: pickTitle(pendingFailedQuiz) })}
                    </p>
                    <p className={`mt-1 text-xs text-amber-800/90 dark:text-amber-300/90`}>
                      {t('placementWizard.failedHint')}
                    </p>
                    <div className={`mt-3 flex flex-wrap gap-2`}>
                      <button
                        type="button"
                        onClick={() => setQuizItem(pendingFailedQuiz)}
                        className="rounded-lg border border-amber-400 bg-white px-3 py-2 text-xs font-semibold text-amber-900 dark:border-amber-700 dark:bg-gray-900 dark:text-amber-200"
                      >
                        {t('placementWizard.retakeQuiz')}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!runId) return;
                          await levelProgressionService.completeKnowledgePlacementRun(runId, 'recommended', pendingFailedQuiz.quizId);
                          const state = await levelProgressionService.getKnowledgePlacementState();
                          setReco(state.lastRecommendation ?? null);
                          setPendingFailedQuiz(null);
                          setFinishedView('recommended');
                          emitKnowledgePlacementUpdated();
                        }}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"
                      >
                        {t('placementWizard.setFittedLevel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPendingFailedQuiz(null);
                          setIndex((i) => Math.max(0, i - 1));
                        }}
                        disabled={index <= 0}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200"
                      >
                        {t('placementWizard.trySimplerQuiz')}
                      </button>
                    </div>
                  </div>
                )}
                {pendingPassedQuiz && (
                  <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
                    <p className={`text-sm font-semibold text-emerald-900 dark:text-emerald-200`}>
                      {t('placementWizard.passedTitle', { title: pickTitle(pendingPassedQuiz) })}
                    </p>
                    <p className={`mt-1 text-xs text-emerald-800/90 dark:text-emerald-300/90`}>
                      {t('placementWizard.passedHint')}
                    </p>
                    <div className={`mt-3 flex flex-wrap gap-2`}>
                      <button
                        type="button"
                        onClick={async () => {
                          setPendingPassedQuiz(null);
                          if (index < chain.length - 1) {
                            setIndex((i) => Math.min(i + 1, chain.length - 1));
                            return;
                          }
                          if (!runId) return;
                          await levelProgressionService.completeKnowledgePlacementRun(runId, 'advanced');
                          emitKnowledgePlacementUpdated();
                          setReco(null);
                          setFinishedView('advanced');
                        }}
                        className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white"
                      >
                        {t('placementWizard.nextQuiz')}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          await handlePlacementConsiderCurrent();
                        }}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"
                      >
                        {t('placementWizard.setFittedLevel')}
                      </button>
                    </div>
                  </div>
                )}
                {!hasPendingDecision && (
                  <div data-tour-id="knowledge-placement-quiz" className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                  <div className={`flex flex-wrap items-center justify-between gap-3`}>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400">{positionLabel}</p>
                      <h2 className="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">{current ? pickTitle(current) : ''}</h2>
                      {current ? (
                        <FlowContextBreadcrumb
                          className="mt-1 text-sm text-gray-600 dark:text-gray-400"
                          contextSubtitle={current.contextSubtitle}
                          contextSubtitleFa={current.contextSubtitleFa}
                          isRtl={isRtl}
                        />
                      ) : null}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{t('placementWizard.openQuizDecide')}</p>
                    </div>
                    <div className={`flex gap-2`}>
                      <button
                        type="button"
                        onClick={goPrevInChain}
                        disabled={index <= 0}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-40 dark:border-gray-600"
                      >
                        {isRtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                        {t('placementWizard.previous')}
                      </button>
                      <button
                        type="button"
                        onClick={goNextInChain}
                        disabled={index >= total - 1}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-40 dark:border-gray-600"
                      >
                        {t('placementWizard.next')}
                        {isRtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800" dir={direction}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all"
                      style={{
                        width: `${total > 0 ? ((index + 1) / total) * 100 : 0}%`,
                        marginInlineStart: isRtl ? 'auto' : undefined,
                      }}
                    />
                  </div>

                  <div className={`mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap `}>
                    <button
                      type="button"
                      data-tour-id="knowledge-placement-take-quiz"
                      onClick={openCurrentQuiz}
                      className="flex-1 rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow hover:bg-violet-700"
                    >
                      {t('placementWizard.takeQuiz')}
                    </button>
                    <button
                      type="button"
                      data-tour-id="knowledge-placement-skip"
                      onClick={() => {
                        void skipTooEasyAndPersist();
                      }}
                      disabled={index >= total - 1}
                      className="flex-1 rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {t('placementWizard.tooEasySkip')}
                    </button>
                  </div>
                  <p className={`mt-4 text-xs text-gray-500 dark:text-gray-400`}>
                    {t('placementWizard.skipTip')}
                  </p>
                  </div>
                )}

                <button
                  type="button"
                  data-tour-id="knowledge-placement-change-course"
                  onClick={() => {
                    setRunId(null);
                    setPendingFailedQuiz(null);
                    setPendingPassedQuiz(null);
                    setIndex(0);
                    goToStep('pick-course');
                  }}
                  className={`text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 block w-full`}
                >
                  {t('placementWizard.changeCourse')}
                </button>
              </>
            )}
          </section>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </ResponsiveContainer>

      {quizItem && (
        <QuizModal
          quizType="lesson"
          quizId={quizItem.quizId}
          contentForceEnglish={isDesignedForNonFarsiUsers(quizItem)}
          onClose={() => setQuizItem(null)}
          onFlowStageComplete={handleQuizFlowComplete}
          disableTimeLimit
          placementDirectStart
          placementPassingScoreOverride={(quizItem as any)?.passingScore}
          onPlacementConsiderCurrent={() => {
            void handlePlacementConsiderCurrent();
          }}
          onPlacementSkipEasy={() => {
            setQuizItem(null);
            void skipTooEasyAndPersist();
          }}
          placementNavigation={{
            canPrev: index > 0,
            canNext: index < total - 1,
            onPrev: () => {
              setIndex((i) => Math.max(0, i - 1));
              const next = chain[Math.max(0, index - 1)];
              setQuizItem(next ?? null);
            },
            onNext: () => {
              const nextIndex = Math.min(total - 1, index + 1);
              setIndex(nextIndex);
              const next = chain[nextIndex];
              setQuizItem(next ?? null);
            }
          }}
        />
      )}
    </div>
      )}
    </>
  );
};
